import React, {useState} from 'react';
import {Offer, OfferData} from "types/Offer";
import {FieldArray, FieldArrayRenderProps, Form, Formik, FormikHelpers, FormikProps} from "formik";
import {DateTime} from "luxon";
import Field from "components/Form/Field";
import FormControls from "components/FormControls";
import FieldHeader from "components/Form/FieldHeader";
import {concatenate} from "services/string";
import {Tab} from '@headlessui/react';
import TabButton from "components/TabButton";
import {ExtendedDurationPicker} from "components/Form/DurationPicker";
import Uploader from "components/Form/Uploader";
import {PhotographIcon} from "@heroicons/react/outline";
import UrlField from "components/Form/UrlField";
import Select, {Option} from "components/Form/Select/Select";
import IconName from "types/IconName";
import {OrganisationPreview} from "types/Organisation";
import ngage from "services/ngage";
import {DurationUnit} from "components/Form/DurationPicker/ExtendedDurationPicker";
import {DateField} from "../../../components/Form/Form/Field";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import {TextAreaField} from "../../../components/Form/Form";
import {ToggleSwitch} from "../../../components/Form/Form/Control";
import LinkEditor from "../../../components/Form/LinkEditor";
import {urlTest} from "../../../services/validation-schemas";
import {LinkAPIData} from "../../../types/Link";
import Button from "../../../components/Button";
import {PlusIcon, TrashIcon} from "@heroicons/react/solid";

enum TermsMode {URL, TEXT}

enum UsesMode {SINGLE, MULTIPLE, UNLIMITED}

enum CodeMode {DYNAMIC, STATIC}

enum ExpiresMode {NEVER, DYNAMIC, STATIC}

enum OrganisationMode {ALL, SELECTED}

type Props = {
  offer?: Offer | null
  onSubmit: (values: OfferData, helpers: FormikHelpers<OfferData>) => any
}

const organisationOption = (organisation: OrganisationPreview): Option => ({
  id: organisation.id,
  name: organisation.name,
  icon: IconName.GLOBE_ALT
})

function OfferForm(props: Props) {
  const [mediaUploading, setMediaUploading] = useState<boolean>(false);
  const [termsMode, setTermsMode] = useState<TermsMode>(props.offer?.terms_text ? TermsMode.TEXT : TermsMode.URL);
  const [usesMode, setUsesMode] = useState<UsesMode>(props.offer ? (props.offer.uses ? (props.offer.uses === 1 ? UsesMode.SINGLE : UsesMode.MULTIPLE) : UsesMode.UNLIMITED) : UsesMode.SINGLE);
  const [codeMode, setCodeMode] = useState<CodeMode>(props.offer?.code ? CodeMode.STATIC : CodeMode.DYNAMIC);
  const [expiresMode, setExpiresMode] = useState<ExpiresMode>(props.offer?.expires_at ? ExpiresMode.STATIC : (props.offer?.valid_for ? ExpiresMode.DYNAMIC : ExpiresMode.NEVER));

  const [organisationMode, setOrganisationMode] = useState<OrganisationMode>((!!props.offer?.organisations?.length) ? OrganisationMode.SELECTED : OrganisationMode.ALL);
  const [selectedOrganisations, setSelectedOrganisations] = useState<Array<Option>>(props.offer?.organisations?.map(organisationOption) || []);

  function selectTermsMode(mode: TermsMode, {setFieldValue}: FormikHelpers<OfferData>) {
    void setFieldValue('terms', '')
    setTermsMode(mode)
  }

  function selectUsesMode(mode: UsesMode, formik: FormikHelpers<OfferData>) {
    const defaultUseCounts: Record<UsesMode, number | null> = {
      [UsesMode.UNLIMITED]: null,
      [UsesMode.MULTIPLE]: 2,
      [UsesMode.SINGLE]: 1,
    }
    formik.setFieldValue('uses', defaultUseCounts[mode]).then(_ => {
      void formik.validateField('uses')
    })
    setUsesMode(mode)
  }

  function selectCodeMode(mode: CodeMode, formik: FormikHelpers<OfferData>) {
    formik.setFieldValue('code', mode === CodeMode.STATIC ? '' : null).then(_ => {
      void formik.validateField('code');
    })
    setCodeMode(mode)
  }

  function selectExpiresMode(mode: ExpiresMode, formik: FormikProps<OfferData>) {
    formik.setFieldValue('expires_at', null).catch(console.error).then(_ => {
      void formik.validateField('expires_at');
    });
    formik.setFieldValue('valid_for', null).catch(console.error).then(_ => {
      void formik.validateField('valid_for');
    });

    setExpiresMode(mode);
  }

  function selectOrganisationMode(mode: OrganisationMode, {setFieldValue}: FormikHelpers<OfferData>) {
    void setFieldValue('organisation_ids', [])
    setSelectedOrganisations([])
    setOrganisationMode(mode)
  }

  const organisationSource = async (query: string) => {
    return (await ngage.organisations.filtered({search: query || null}))?.data.map(organisationOption) || []
  }

  const handleSelectOrganisations = (organisations: Array<Option>, formik: FormikProps<OfferData>) => {
    void formik.setFieldValue('organisation_ids', organisations.map(organisation => organisation.id))
    setSelectedOrganisations(organisations)
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("This field is required").max(255, 'Maximum of 255 characters'),
    description: Yup.string().max(50000, 'Maximum character limit exceeded'),
    incentive: Yup.string().required("This field is required").max(255, 'Maximum of 255 characters'),
    terms: termsMode === TermsMode.TEXT
      ? Yup.string().required("This field is required").max(50000, 'Maximum character limit exceeded')
      : Yup.string()
        .matches(
          /^https?:\/\//,
          'URL must start with http:// or https://'
        )
        .url('URL is not valid')
        .required("This field is required"),
    uses: usesMode === UsesMode.MULTIPLE
      ? Yup.number()
        .required("This field is required")
        .min(2, 'Minimum of 2')
        .max(255, 'Maximum of 255')
        .nullable()
      : Yup.number().required("This field is required").nullable(),
    code: Yup.string()
      .required("This field is required")
      .nullable()
      .max(50, "Maximum of 50 characters"),
    valid_at: Yup.date().required("This field is required"),
    valid_for: expiresMode === ExpiresMode.DYNAMIC
      ? Yup.string().required("This field is required")
      : Yup.string().nullable(),
    expires_at: expiresMode === ExpiresMode.STATIC
      ? Yup.date().required("This field is required")
      : Yup.date().nullable(),
    links: Yup.array().of(
      Yup.object().shape({
        text: Yup.string().required('This field is required').max(100, 'Maximum 100 characters'),
        url: Yup.string()
          .required('This field is required')
          .max(250, 'Maximum 250 characters')
          .test('valid-url', urlTest),
      })
    ),
  })

  const initialValues: OfferData = {
    name: props.offer?.name || '',
    incentive: props.offer?.incentive || '',
    description: props.offer?.description || '',
    image_id: props.offer?.image?.id || null,
    terms: props.offer?.terms || '',
    uses: props.offer ? props.offer?.uses : 1,
    code: props.offer?.code || null,
    valid_at: props.offer?.valid_at.toUTC().toISO() || DateTime.utc().startOf('minute').toISO(),
    valid_for: props.offer?.valid_for?.toISO() || undefined,
    expires_at: props.offer?.expires_at?.toUTC().toISO() || undefined,
    organisation_ids: props.offer?.organisations?.map(org => org.id) || [],
    links: props.offer?.links.map(attachedLink => ({
      id: attachedLink.id,
      text: attachedLink.text,
      url: attachedLink.url,
      options: attachedLink.options,
    })),
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={props.onSubmit}>
      {formik => {
        return (
          <Form className="space-y-8 divide-y divide-gray-200" noValidate>
            <ErrorNotification formik={formik} />
  
            <div className="shadow sm:rounded-md">
              <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                <div className="space-y-8 divide-y divide-gray-200">
                  <div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Key Information
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Basic details information about the offered incentive
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
  
                      {/* Name */}
                      <div className="sm:col-span-4">
                        <Field
                          type="text"
                          label="Name *"
                          hint='255 chars max'
                          name="name"
                          id="name"
                          maxLength={255}
                          autoFocus
                        />
                      </div>
  
                      {/* Incentive */}
                      <div className="sm:col-span-5">
                        <Field
                          type="text"
                          label="Incentive *"
                          hint='255 chars max'
                          name="incentive"
                          id="incentive"
                          maxLength={255}
                        />
                      </div>
  
                      {/* Description */}
                      <div className="sm:col-span-6">
                        <TextAreaField
                          label="Description"
                          hint="Optional. 50,000 chars max."
                          name="description"
                          id="description"
                          rows={8}
                          maxChars={50000}
                        />
                      </div>
  
                      {/* Image */}
                      <div className="sm:col-span-6">
                        <FieldHeader label="Image" hint="Optional"/>
                        <Uploader
                          icon={PhotographIcon}
                          type={Uploader.types.IMAGE}
                          files={props.offer?.image}
                          maxFiles={1}
                          onChange={files => formik.setFieldValue('image_id', files[0] ? files[0].id : null)}
                          onUploadStart={() => setMediaUploading(true)}
                          onUploadEnd={() => setMediaUploading(false)}
                        />
                      </div>
  
                      {/* Terms & Conditions */}
                      <div className={concatenate(termsMode === TermsMode.URL ? 'sm:col-span-4' : 'sm:col-span-6')}>
                        <FieldHeader label="Terms & Conditions *"/>
                        <Tab.Group selectedIndex={termsMode} onChange={index => selectTermsMode(index, formik)}>
                          <Tab.List className="mt-1 mb-1 relative z-0 inline-flex shadow-sm rounded-md">
                            <TabButton position={TabButton.positions.FIRST}>URL</TabButton>
                            <TabButton position={TabButton.positions.LAST}>Text</TabButton>
                          </Tab.List>
                          <Tab.Panels>
                            <Tab.Panel className={'focus:outline-brand'}>
                              <UrlField
                                formik={formik}
                                id='terms'
                                name='terms'
                              />
                            </Tab.Panel>
  
                            <Tab.Panel>
                              <TextAreaField
                                name="terms"
                                id="terms"
                                hint='Optional. 50,000 chars max.'
                                rows={8}
                                maxChars={50000}
                              />
                            </Tab.Panel>
                          </Tab.Panels>
                        </Tab.Group>
                      </div>

                      {/* Links */}
                      <div className="sm:col-span-8">
                        <ToggleSwitch
                          label="Links"
                          description='Maximum of 2'
                          checked={!!formik.values.links?.length}
                          onChange={enabled => formik.setFieldValue('links', enabled? [{
                            text: '',
                            url: ''
                          }] : [])}
                        />

                        {!!formik.values.links?.length &&
                          <div className="mt-4">
                            <FieldArray name={'links'}>
                              {({push, remove, form}: FieldArrayRenderProps) => (
                                <div className="flex flex-col gap-4 items-start">
                                  <ul className="flex flex-col gap-4 self-stretch">
                                    {form.values.links.map((_link: LinkAPIData, index: number) => (
                                      <li key={index} className="flex bg-gray-50 rounded-md pt-4 pr-4 items-start">
                                        <div className="flex-grow">
                                          <LinkEditor name={`links.${index}`} />
                                        </div>

                                          <Button
                                            size={Button.sizes.X_SMALL}
                                            color={Button.colors.WHITE}
                                            icon={TrashIcon}
                                            onClick={() => remove(index)}
                                          />
                                      </li>
                                    ))}
                                  </ul>

                                  <Button
                                    color={Button.colors.SECONDARY}
                                    icon={PlusIcon}
                                    size={Button.sizes.TINY}
                                    onClick={() => push({text: '', url: ''})}
                                    disabled={formik.values.links && formik.values.links.length >= 2}
                                  >
                                    Add Link
                                  </Button>
                                </div>
                              )}
                            </FieldArray>
                          </div>
                        }
                      </div>
                    </div>
                  </div>
  
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Usage
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Set how this voucher can be used
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
  
                      {/* Usage per person */}
                      <div className="sm:col-span-6">
                        <FieldHeader label="Uses per Person *"/>
                        <Tab.Group selectedIndex={usesMode} onChange={index => selectUsesMode(index, formik)}>
                          <Tab.List className="mt-1 mb-1 relative z-0 inline-flex shadow-sm rounded-md">
                            <TabButton position={TabButton.positions.FIRST}>Single</TabButton>
                            <TabButton position={TabButton.positions.MIDDLE}>Multiple</TabButton>
                            <TabButton position={TabButton.positions.LAST}>Unlimited</TabButton>
                          </Tab.List>
                          <Tab.Panels>
                            <Tab.Panel/>
                            <Tab.Panel className="w-44">
                              <Field
                                type="number"
                                addon="uses"
                                name="uses"
                                id="uses"
                              />
                            </Tab.Panel>
                            <Tab.Panel/>
                          </Tab.Panels>
                        </Tab.Group>
                      </div>
  
                      {/* Code */}
                      <div className="sm:col-span-3">
                        <FieldHeader label="Code *"/>
                        <Tab.Group selectedIndex={codeMode} onChange={index => selectCodeMode(index, formik)}>
                          <Tab.List className="mt-1 mb-1 relative z-0 inline-flex shadow-sm rounded-md">
                            <TabButton position={TabButton.positions.FIRST}>Dynamic</TabButton>
                            <TabButton position={TabButton.positions.LAST}>Static</TabButton>
                          </Tab.List>
                          <Tab.Panels>
                            <Tab.Panel/>
                            <Tab.Panel>
                              <Field
                                type="text"
                                name="code"
                                id="code"
                                maxLength={50}
                              />
                            </Tab.Panel>
                          </Tab.Panels>
                        </Tab.Group>
                      </div>
                    </div>
                  </div>
  
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Validity
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Choose when this voucher becomes valid and when it expires
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
  
                      {/* Valid At */}
                      <div className="sm:col-span-3 lg:col-span-2">
                        <FieldHeader label="Valid At" fieldId="valid_at"/>
                        <div className="mt-1">
                          <DateField
                            type='datetime-local'
                            name='valid_at'
                            id='valid_at'
                            autoComplete='off'
                          />
                        </div>
                      </div>
  
                      {/* Expiry */}
                      <div className="sm:col-span-6 sm:col-start-1">
                        <FieldHeader label="Expires"/>
                        <Tab.Group selectedIndex={expiresMode} onChange={index => selectExpiresMode(index, formik)}>
                          <Tab.List className="mt-1 mb-1 relative z-0 inline-flex shadow-sm rounded-md">
                            <TabButton position={TabButton.positions.FIRST}>Never</TabButton>
                            <TabButton position={TabButton.positions.MIDDLE}>Dynamic</TabButton>
                            <TabButton position={TabButton.positions.LAST}>Static</TabButton>
                          </Tab.List>
                          <Tab.Panels>
                            <Tab.Panel/>
                            <Tab.Panel className="lg:flex lg:items-center lg:space-x-2">
                              <span className="text-sm">After</span>
                              <ExtendedDurationPicker
                                units={[DurationUnit.YEAR, DurationUnit.MONTH, DurationUnit.WEEK, DurationUnit.DAY]}
                                value={formik.values.valid_for}
                                onChange={duration => formik.setFieldValue('valid_for', duration.toISO())}
                              />

                              {(!!formik.errors.valid_for && formik.touched.valid_for) &&
                                <FieldHelp isError>{formik.errors.valid_for}</FieldHelp>
                              }
                            </Tab.Panel>
                            <Tab.Panel className="sm:grid sm:grid-cols-6">
                              <div className="sm:col-span-2">
                                <DateField
                                  type='datetime-local'
                                  name='expires_at'
                                  id='expires_at'
                                  autoComplete='off'
                                  min={DateTime.fromISO(formik.values.valid_at).plus({minute: 1}).toISO()}
                                />
                              </div>
                            </Tab.Panel>
                          </Tab.Panels>
                        </Tab.Group>
                      </div>
                    </div>
                  </div>
  
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Organisations
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Organisations to which this offer relates
                      </p>
                    </div>
                    <div className="mt-6">
  
                      <Tab.Group selectedIndex={organisationMode}
                                 onChange={index => selectOrganisationMode(index, formik)}>
                        <Tab.List className="mt-1 mb-1 relative z-0 inline-flex shadow-sm rounded-md">
                          <TabButton position={TabButton.positions.FIRST}>All</TabButton>
                          <TabButton position={TabButton.positions.LAST}>Selected</TabButton>
                        </Tab.List>
                        <Tab.Panels>
                          <Tab.Panel/>
                          <Tab.Panel className="mt-2 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                            {/* Organisations */}
                            <div className="sm:col-span-6">
                              <Select
                                label="Organisations"
                                hint="Type to search"
                                source={organisationSource}
                                selected={selectedOrganisations}
                                onChange={selected => handleSelectOrganisations(selected, formik)}
                                multiple
                              />
                            </div>
                          </Tab.Panel>
                        </Tab.Panels>
                      </Tab.Group>
                    </div>
                  </div>
                </div>
              </div>
  
              <FormControls formik={formik} preventSubmit={mediaUploading}/>
            </div>
          </Form>
        )
      }}
    </Formik>
  );
}

export default OfferForm;